export const PRICING_CURRENCY = "$";

export type Currency = "USD" | "INR";

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: "$",
  INR: "₹",
};

export const CURRENCY_MULTIPLIERS: Record<Currency, number> = {
  USD: 1,
  INR: 88,
};

export function getPriceForCurrency(usdPrice: number, currency: Currency): number {
  return usdPrice * CURRENCY_MULTIPLIERS[currency];
}

export function getCurrencySymbol(currency: Currency): string {
  return CURRENCY_SYMBOLS[currency];
}

/** Sound effects price multiplier applied to TTS price per 1k characters */
export const SOUND_EFFECTS_PRICE_MULTIPLIER = 0.6;

/** Flash/Turbo TTS price multiplier used on the API platform (half the cost of multilingual) */
export const FLASH_TURBO_PRICE_MULTIPLIER = 0.5;

/** Music finetune cost in USD */
export const MUSIC_FINETUNE_USD = 1.5;

/** Legacy Eleven Music pricing (control) */
export const MUSIC_PRICING_LEGACY = {
  creativeCreditsPerMinute: 1_500,
  apiUsdPerMinute: 0.3,
} as const;

/** Updated Eleven Music pricing (feature flag variant) */
export const MUSIC_PRICING_V2 = {
  creativeCreditsPerMinute: 900,
  apiUsdPerMinute: 0.15,
} as const;

/** PostHog flag keys that affect pricing display/calculation */
export const PRICING_FEATURE_FLAGS = {
  MUSIC_PRICING_V2: "mw-music-pricing-v2",
} as const;

export type PricingFeatureFlagKey = (typeof PRICING_FEATURE_FLAGS)[keyof typeof PRICING_FEATURE_FLAGS];
export type PricingFeatureFlags = Partial<Record<PricingFeatureFlagKey, boolean>>;

export type PricingDataOptions = {
  featureFlags?: PricingFeatureFlags;
};

type DiscountMessageKey = "first_month_half_price" | "two_months_free" | "first_month_one_dollar";

// --- Core Value Types ---

/** Price in USD. undefined = not available on this tier */
export type Price = number | undefined;

/** Limit amount. Infinity = unlimited, undefined = not available */
export type Limit = number | undefined;

// --- Product-Specific Configs ---
// Structure: enabled, features (only for varying features), limits, pricing

export interface TextToSpeechConfig {
  enabled: boolean;
  // No features - highQuality and turbo available on all tiers
  limits: {
    highQualityMinutes: Limit;
    turboMinutes: Limit;
    characterLimit: Limit;
    concurrencyLimit: number;
    priority: number;
  };
  pricing: {
    overagePerThousandHighQuality: Price;
    overagePerThousandTurbo: Price;
    overagePerThousandHighQualityAnnual?: Price;
    overagePerThousandTurboAnnual?: Price;
  };
}

export interface SpeechToSpeechConfig {
  enabled: boolean;
  limits: {
    includedMinutes: Limit;
  };
  pricing?: {
    /* Overrides the dynamically calculated price per minute */
    pricePerMinuteOverride?: Price;
  };
}

export interface SpeechToTextConfig {
  enabled: boolean;
  limits: {
    apiHours: Limit;
    uiHours: Limit;
  };
  pricing: {
    apiPricePerHour: Price;
    apiAdditionalPricePerHour: Price;
    uiAdditionalPricePerHour: Price;
    keytermPromptingBaseMultiplier?: Price;
    entityDetectionBaseMultiplier?: Price;
  };
}

export interface SpeechToTextRealtimeConfig {
  enabled: boolean; // varies: false on free
  limits: {
    includedHours: Limit;
  };
  pricing: {
    pricePerHour: Price;
    additionalPricePerHour: Price;
  };
}

export interface ConversationalAIConfig {
  enabled: boolean;
  limits: {
    includedMinutes: Limit;
    concurrencyLimit: number;
  };
  pricing: {
    /** ElevenLabs hosting cost per minute for calls */
    hostingCostPerMinute?: Price;
    /** Extra minute cost for additional usage */
    extraMinuteCost: Price;
    extraMinuteCostAnnual?: Price;
    /** Text message cost */
    textMessageCost: Price;
  };
}

export interface DubbingConfig {
  enabled: boolean;
  features: {
    /** No watermark option available (varies: false on free) */
    noWatermark: boolean;
    /** Dubbing Studio available */
    studio: boolean;
  };
  limits: {
    automaticWatermarkMinutes: Limit;
    automaticNoWatermarkMinutes: Limit;
    studioWatermarkMinutes: Limit;
    studioNoWatermarkMinutes: Limit;
  };
  pricing: {
    /** Cost per included minute with watermark */
    pricePerMinuteWithWatermark: Price;
    /** Cost per additional minute with watermark */
    additionalMinuteWithWatermark: Price;
    /** Cost per included minute without watermark */
    pricePerMinuteWithoutWatermark: Price;
    /** Cost per additional minute without watermark */
    additionalMinuteWithoutWatermark: Price;
  };
}

export interface SoundEffectsConfig {
  enabled: boolean;
  limits: {
    includedSeconds: Limit;
    /** Number of sound effect generations included */
    includedGenerations: Limit;
  };
  /** Whether overage pricing is available (Creator tier and above) */
  hasOveragePricing?: boolean;
  pricing?: {
    /* Overrides the dynamically calculated price per minute */
    pricePerMinuteOverride?: Price;
  };
}

export interface MusicConfig {
  enabled: boolean;
  features: {
    /** Commercial use allowed:
     * - false: No commercial use (free tier)
     * - "excluding_enterprise_and_streaming": Yes, excluding enterprise use cases and streaming services (starter)
     * - "excluding_enterprise": Yes, excluding enterprise use cases (creator+)
     */
    commercialUse: false | "excluding_enterprise_and_streaming" | "excluding_enterprise";
  };
  limits: {
    includedMinutes: Limit;
    concurrencyLimit?: number;
    priority?: number;
  };
  pricing: {
    /** Cost per included minute (effective rate) */
    pricePerMinute: Price;
  };
  /** Whether overage pricing is available (Creator tier and above) */
  hasOveragePricing?: boolean;
}

export interface VoiceIsolatorConfig {
  enabled: boolean;
  limits: {
    includedMinutes: Limit;
  };
  /** Whether overage pricing is available (Creator tier and above) */
  hasOveragePricing?: boolean;
  pricing?: {
    /* Overrides the dynamically calculated price per minute */
    pricePerMinuteOverride?: Price;
  };
}

export interface VoiceChangerConfig {
  enabled: boolean;
  limits: {
    includedMinutes: Limit;
  };
  /** Whether overage pricing is available (Creator tier and above) */
  hasOveragePricing?: boolean;
  pricing?: {
    /* Overrides the dynamically calculated price per minute */
    pricePerMinuteOverride?: Price;
  };
}

export interface ProjectsConfig {
  enabled: boolean;
  limits: {
    maxProjects: Limit;
  };
}

export interface AudioNativeConfig {
  enabled: boolean; // varies: false on free/starter
  limits: {
    maxEmbeds: Limit;
  };
}

export interface ImageVideoConfig {
  enabled: boolean;
  limits: {
    images: Limit;
    videoSeconds: Limit;
    upscalingImages: Limit;
    upscalingVideoSeconds: Limit;
    lipsyncSeconds: Limit;
  };
}

export interface ProductionsConfig {
  enabled: boolean;
  pricing: {
    transcriptsPerMinute: Price;
    captionsPerMinute: Price;
    subtitlesPerMinute: Price;
    dubsPerMinute: Price;
    audiobooksPerHour: Price;
  };
}

export interface VoicesConfig {
  enabled: boolean;
  features: {
    /** Instant voice cloning (varies: false on free) */
    instantVoiceCloning: boolean;
    /** Professional voice cloning (varies: false on free/starter) */
    professionalVoiceCloning: boolean;
  };
  limits: {
    pvcSlots: Limit;
    customVoices: Limit;
  };
}

// --- All Products Map Type ---

export interface TierProducts {
  textToSpeech: TextToSpeechConfig;
  speechToSpeech: SpeechToSpeechConfig;
  speechToText: SpeechToTextConfig;
  speechToTextRealtime: SpeechToTextRealtimeConfig;
  conversationalAI: ConversationalAIConfig;
  dubbing: DubbingConfig;
  soundEffects: SoundEffectsConfig;
  music: MusicConfig;
  voiceIsolator: VoiceIsolatorConfig;
  voiceChanger: VoiceChangerConfig;
  voices: VoicesConfig;
  projects: ProjectsConfig;
  audioNative: AudioNativeConfig;
  imageVideo: ImageVideoConfig;
  productions?: ProductionsConfig;
}

// --- Capabilities (Cross-cutting features) ---

export interface TierCapabilities {
  /** Commercial license for generated content */
  commercialLicense: boolean;
  /** No attribution required */
  noAttributionRequired: boolean;
  /** Access to usage analytics */
  usageAnalytics: boolean;
  /** Multiple seats available */
  multipleSeats: boolean;
  /** Default number of seats */
  seats?: number;
}

// --- API Configuration ---

export interface TierApiConfig {
  /** Supported audio formats */
  formats: string;
  /** Audio quality description */
  audioQuality?: string;
}

// --- Plan Pricing ---

export interface SubscriptionPricing {
  monthly: {
    price: number;
    discountedPrice?: number;
    discountMessageKey?: DiscountMessageKey;
  };
  yearly: {
    price: number;
    discountedPrice?: number;
    discountMessageKey?: DiscountMessageKey;
  };
}

/** @deprecated Use SubscriptionPricing instead */
export type PlanPricing = SubscriptionPricing;

export interface TierPricing {
  /** Subscription pricing (undefined for enterprise/contact sales) */
  subscription?: SubscriptionPricing;
  /** Included credits per month (shared across all products) */
  includedCredits: number;
  /** Cost per 1000 additional credits (for overage) */
  additionalCreditsPerThousand: {
    monthly: Price;
    annual?: Price;
  };
}

export type TierId = "free" | "starter" | "creator" | "pro" | "scale" | "scale_2024_08_10" | "enterprise" | "payg";

// Tier display key types - derived from TierId to ensure type safety
export type TierTitleKey = `tiers.${TierId}.title`;
export type TierDescriptionKey = `tiers.${TierId}.description`;
export type TierFeaturesSummaryLabelKey = `tiers.${TierId}.features_summary_label`;
export type TierAudioQualityKey = `tiers.${TierId}.audio_quality`;

// --- Platform-Separated Pricing Config ---
// These interfaces separate pricing concerns by the platform the user is on.

/**
 * Credit consumption rates for the Creative platform.
 * Dollar prices are derived: dollarPrice = creditRate × (subscriptionPrice / includedCredits).
 * Undefined values indicate the product/feature is unavailable on that tier.
 */
export interface CreativePlatformConfig {
  textToSpeech: {
    /** Credits consumed per 1,000 characters for non-turbo/multilingual TTS */
    nonTurboCreditsPerThousandChars: number;
    /** Credits consumed per 1,000 characters for turbo/flash TTS */
    turboCreditsPerThousandChars: number;
    /**
     * Escape hatch: explicit included minutes for multilingual TTS.
     * Set only for tiers where included units can't be derived from credits (e.g. Free, PAYG).
     */
    overrideIncludedMultilingualMinutes?: number;
    /** Escape hatch: explicit included minutes for turbo/flash TTS. */
    overrideIncludedTurboMinutes?: number;
  };
  speechToText: {
    /** Credits consumed per minute of speech-to-text transcription */
    creditsPerMinute: number;
    /** Escape hatch: explicit included hours for STT. */
    overrideIncludedHours?: number;
  };
  voiceChanger: {
    creditsPerMinute: number;
    /** Escape hatch: explicit included minutes for voice changer (also used for speech-to-speech rows). */
    overrideIncludedMinutes?: number;
  };
  voiceIsolator: {
    creditsPerMinute: number;
    /** Escape hatch: explicit included minutes for voice isolator. */
    overrideIncludedMinutes?: number;
  };
  soundEffects: {
    creditsPerGeneration: number;
    /** Escape hatch: explicit included generations for sound effects. */
    overrideIncludedGenerations?: number;
  };
  music: {
    creditsPerMinute: number;
    /** Escape hatch: explicit included minutes for music. */
    overrideIncludedMinutes?: number;
  };
  dubbing: {
    autoWatermarkCreditsPerMinute: number;
    /** undefined = not available on this tier */
    autoNoWatermarkCreditsPerMinute?: number;
    studioWatermarkCreditsPerMinute: number;
    /** undefined = not available on this tier */
    studioNoWatermarkCreditsPerMinute?: number;
    /** Escape hatch: explicit included minutes for automatic dubbing with watermark. */
    overrideIncludedAutoWatermarkMinutes?: number;
    /** Escape hatch: explicit included minutes for automatic dubbing without watermark. */
    overrideIncludedAutoNoWatermarkMinutes?: number;
    /** Escape hatch: explicit included minutes for dubbing studio with watermark. */
    overrideIncludedStudioWatermarkMinutes?: number;
    /** Escape hatch: explicit included minutes for dubbing studio without watermark. */
    overrideIncludedStudioNoWatermarkMinutes?: number;
  };
}

/**
 * Fixed USD rates for the API platform.
 * These are the same across all tiers.
 */
export interface ApiPlatformConfig {
  textToSpeech: {
    turboUsdPer1000Chars: number;
    multilingualUsdPer1000Chars: number;
  };
  speechToText: {
    scribeUsdPerHour: number;
    scribeRealtimeUsdPerHour: number;
    entityDetectionUsdPerHour: number;
    keytermPromptingUsdPerHour: number;
  };
  music: { usdPerMinute: number };
  voiceIsolator: { usdPerMinute: number };
  voiceChanger: { usdPerMinute: number };
  soundEffects: { usdPerMinute: number };
  dubbing: {
    autoUsdPerMinute: number;
    autoNoWatermarkUsdPerMinute: number;
    studioUsdPerMinute: number;
  };
}

/**
 * Fixed USD rates for the Agents platform, plus per-tier limits.
 */
export interface AgentsPlatformConfig {
  audioUsdPerMinute: number;
  audioBurstUsdPerMinute: number;
  textMessageUsdPerMessage: number;
  includedMinutes: number;
  concurrencyLimit: number;
}

/**
 * Platform-separated pricing configuration.
 * All tiers include this config alongside the legacy `products` field.
 */
export interface TierPlatformConfig {
  creative: CreativePlatformConfig;
  api: ApiPlatformConfig;
  agents: AgentsPlatformConfig;
}

export interface Tier {
  /** Unique tier identifier */
  planId: TierId;
  /** Plan type: individual or company */
  type: "individual" | "company";

  /** All pricing information */
  pricing: TierPricing;

  /** All product configurations */
  products: TierProducts;

  /** Cross-cutting capabilities */
  capabilities: TierCapabilities;

  /** API configuration */
  api: TierApiConfig;

  /** Call-to-action link */
  cta: {
    href: string;
  };

  /** Display information for pricing cards */
  display: {
    titleKey: TierTitleKey;
    descriptionKey: TierDescriptionKey;
    featuresSummaryLabelKey: TierFeaturesSummaryLabelKey;
  };

  /** Platform-separated pricing config. */
  platformConfig: TierPlatformConfig;
}

/**
 * A single feature bullet in a pricing card.
 * Some entries carry a `params` function that resolves
 * runtime values (e.g. included minutes) from the tier at render time.
 */
export interface PricingCardFeatureItem {
  feature_name_translation_key: string;
  params?: (tier: Tier) => Record<string, number>;
}

/**
 * Returns the number of agent minutes included for a tier.
 *
 * The minutes are derived from the monthly subscription price divided by the
 * audio cost per minute — matching the "included minutes = price / rate" model.
 * Falls back to the hardcoded `agents.includedMinutes` for tiers without a
 * subscription price (free, enterprise, PAYG) or a zero rate (enterprise).
 */
export function getAgentIncludedMinutes(tier: Tier): number {
  const agents = tier.platformConfig.agents;
  const monthlyPrice = tier.pricing.subscription?.monthly.price;
  if (monthlyPrice && agents.audioUsdPerMinute > 0) {
    return Math.round(monthlyPrice / agents.audioUsdPerMinute);
  }
  return agents.includedMinutes;
}

// Shared agent-platform feature items — use parameterized translation keys so
// the actual numbers come from each tier's platformConfig at render time.
const V2_AGENTS_CALL_MINUTES: PricingCardFeatureItem = {
  feature_name_translation_key: "tiers.common.agents_platform.features.call_minutes",
  params: (tier: Tier) => ({ minutes: getAgentIncludedMinutes(tier) }),
};

const V2_AGENTS_CONCURRENT_CALLS: PricingCardFeatureItem = {
  feature_name_translation_key: "tiers.common.agents_platform.features.concurrent_calls",
  params: (tier: Tier) => ({ calls: tier.platformConfig.agents.concurrencyLimit }),
};

const V2_PROFESSIONAL_VOICE_CLONES: PricingCardFeatureItem = {
  feature_name_translation_key: "tiers.common.features.professional_voice_clones",
  params: (tier: Tier) => ({ count: tier.products.voices.limits.pvcSlots ?? 0 }),
};

const V2_WORKSPACE_SEATS: PricingCardFeatureItem = {
  feature_name_translation_key: "tiers.common.features.workspace_seats",
  params: (tier: Tier) => ({ count: tier.capabilities.seats ?? 0 }),
};

// Pricing Card Content - defined as const to preserve literal types for translation keys
export const PRICING_CARD_CONTENT = {
  free: {
    simple: {
      title: "tiers.free.title",
      description: "tiers.free.description",
      features_summary_label: "tiers.free.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.free.simple.features.text_to_speech" },
        { feature_name_translation_key: "tiers.free.simple.features.speech_to_text" },
        { feature_name_translation_key: "tiers.free.simple.features.music" },
        { feature_name_translation_key: "tiers.free.simple.features.agents" },
        { feature_name_translation_key: "tiers.free.simple.features.projects_in_studio" },
        { feature_name_translation_key: "tiers.free.simple.features.automated_dubbing" },
        { feature_name_translation_key: "tiers.free.simple.features.api_access" },
      ],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.free.title",
      description: "tiers.free.description",
      features_summary_label: "tiers.free.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.free.creative_platform.features.text_to_speech" },
        { feature_name_translation_key: "tiers.free.creative_platform.features.speech_to_text" },
        { feature_name_translation_key: "tiers.free.creative_platform.features.sound_effects" },
        { feature_name_translation_key: "tiers.free.creative_platform.features.voice_design" },
        { feature_name_translation_key: "tiers.free.creative_platform.features.music" },
        { feature_name_translation_key: "tiers.free.creative_platform.features.productions" },
        { feature_name_translation_key: "tiers.free.creative_platform.features.image_video" },
        { feature_name_translation_key: "tiers.free.creative_platform.features.studio" },
      ],
      credits_usable: [
        { usable_for_translation_key: "tiers.free.creative_platform.credits_usable.text_to_speech_minutes" },
        { usable_for_translation_key: "tiers.free.creative_platform.credits_usable.music_minutes" },
        { usable_for_translation_key: "tiers.free.creative_platform.credits_usable.sound_effects_seconds" },
      ],
    },
    agents_platform: {
      title: "tiers.free.title",
      description: "tiers.free.description_agents",
      features_summary_label: "tiers.free.features_summary_label",
      features: [V2_AGENTS_CALL_MINUTES, V2_AGENTS_CONCURRENT_CALLS] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    api: {
      title: "tiers.free.title",
      description: "tiers.free.description_api",
      features_summary_label: "tiers.free.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.free.api.features.text_to_speech_api" },
        { feature_name_translation_key: "tiers.free.api.features.speech_to_text_api" },
        { feature_name_translation_key: "tiers.free.api.features.voice_isolator_api" },
        { feature_name_translation_key: "tiers.free.api.features.voice_changer_api" },
        { feature_name_translation_key: "tiers.free.api.features.dubbing_api" },
        { feature_name_translation_key: "tiers.free.api.features.agents" },
        { feature_name_translation_key: "tiers.free.api.features.api_access" },
      ],
      credits_usable: [
        { usable_for_translation_key: "credits_usable_for_high_quality_text_to_speech" },
        { usable_for_translation_key: "credits_usable_for_conversational_ai" },
      ],
    },
  },
  starter: {
    simple: {
      title: "tiers.starter.title",
      description: "tiers.starter.description",
      features_summary_label: "tiers.starter.simple.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.starter.simple.features.commercial_license" },
        { feature_name_translation_key: "tiers.starter.simple.features.instant_voice_cloning" },
        { feature_name_translation_key: "tiers.starter.simple.features.projects_in_studio" },
        { feature_name_translation_key: "tiers.starter.simple.features.dubbing_studio" },
        { feature_name_translation_key: "tiers.starter.simple.features.music_commercial_use" },
      ],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.starter.title",
      description: "tiers.starter.description",
      features_summary_label: "tiers.starter.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.starter.creative_platform.features.commercial_license" },
        { feature_name_translation_key: "tiers.starter.creative_platform.features.instant_voice_cloning" },
        { feature_name_translation_key: "tiers.starter.creative_platform.features.more_projects_in_studio" },
        { feature_name_translation_key: "tiers.starter.creative_platform.features.music_for_socials_and_ads" },
        { feature_name_translation_key: "tiers.starter.creative_platform.features.dubbing_studio" },
      ],
      credits_usable: [
        { usable_for_translation_key: "tiers.starter.creative_platform.credits_usable.text_to_speech_minutes" },
        { usable_for_translation_key: "tiers.starter.creative_platform.credits_usable.music_minutes" },
        { usable_for_translation_key: "tiers.starter.creative_platform.credits_usable.sound_effects_seconds" },
      ],
    },
    agents_platform: {
      title: "tiers.starter.title",
      description: "tiers.starter.description_agents",
      features_summary_label: "tiers.starter.features_summary_label",
      features: [
        V2_AGENTS_CALL_MINUTES,
        V2_AGENTS_CONCURRENT_CALLS,
        { feature_name_translation_key: "tiers.starter.agents_platform.features.text_messages" },
        { feature_name_translation_key: "tiers.starter.agents_platform.features.commercial_license" },
      ] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    api: {
      title: "tiers.starter.title",
      description: "tiers.starter.description_api",
      features_summary_label: "tiers.starter.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.starter.api.features.commercial_license" },
        { feature_name_translation_key: "tiers.starter.api.features.instant_voice_cloning" },
        { feature_name_translation_key: "tiers.starter.api.features.dubbing_studio_api" },
        { feature_name_translation_key: "tiers.starter.api.features.eleven_music_api" },
      ],
      credits_usable: [
        { usable_for_translation_key: "credits_usable_for_high_quality_text_to_speech" },
        { usable_for_translation_key: "credits_usable_for_conversational_ai" },
      ],
    },
  },
  creator: {
    simple: {
      title: "tiers.creator.title",
      description: "tiers.creator.description",
      features_summary_label: "tiers.creator.simple.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.creator.simple.features.professional_voice_cloning" },
        { feature_name_translation_key: "tiers.creator.simple.features.additional_credits" },
      ] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.creator.title",
      description: "tiers.creator.description",
      features_summary_label: "tiers.creator.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.creator.creative_platform.features.professional_voice_cloning" },
        {
          feature_name_translation_key:
            "tiers.creator.creative_platform.features.usage_based_billing_for_additional_credits",
        },
      ] as PricingCardFeatureItem[],
      credits_usable: [
        { usable_for_translation_key: "tiers.creator.creative_platform.credits_usable.text_to_speech_minutes" },
        { usable_for_translation_key: "tiers.creator.creative_platform.credits_usable.music_minutes" },
        { usable_for_translation_key: "tiers.creator.creative_platform.credits_usable.sound_effects_seconds" },
      ],
    },
    agents_platform: {
      title: "tiers.creator.title",
      description: "tiers.creator.description_agents",
      features_summary_label: "tiers.creator.features_summary_label",
      features: [
        V2_AGENTS_CALL_MINUTES,
        V2_AGENTS_CONCURRENT_CALLS,
        { feature_name_translation_key: "tiers.creator.agents_platform.features.additional_minutes" },
      ] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    api: {
      title: "tiers.creator.title",
      description: "tiers.creator.description_api",
      features_summary_label: "tiers.creator.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.creator.api.features.professional_voice_cloning" },
        { feature_name_translation_key: "tiers.creator.api.features.usage_based_billing_for_additional_credits" },
      ] as PricingCardFeatureItem[],
      credits_usable: [
        { usable_for_translation_key: "credits_usable_for_high_quality_text_to_speech" },
        { usable_for_translation_key: "credits_usable_for_conversational_ai" },
      ],
    },
  },
  pro: {
    simple: {
      title: "tiers.pro.title",
      description: "tiers.pro.description",
      features_summary_label: "tiers.pro.simple.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.pro.simple.features.high_fidelity_audio" },
        { feature_name_translation_key: "tiers.pro.simple.features.higher_quality_audio_192_kbps" },
      ] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.pro.title",
      description: "tiers.pro.description",
      features_summary_label: "tiers.pro.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.pro.simple.features.high_fidelity_audio" },
        { feature_name_translation_key: "tiers.pro.creative_platform.features.higher_quality_audio_192_kbps" },
      ] as PricingCardFeatureItem[],
      credits_usable: [
        { usable_for_translation_key: "tiers.pro.creative_platform.credits_usable.text_to_speech_minutes" },
        { usable_for_translation_key: "tiers.pro.creative_platform.credits_usable.music_minutes" },
        { usable_for_translation_key: "tiers.pro.creative_platform.credits_usable.sound_effects_seconds" },
      ],
    },
    agents_platform: {
      title: "tiers.pro.title",
      description: "tiers.pro.description_agents",
      features_summary_label: "tiers.pro.features_summary_label",
      features: [V2_AGENTS_CALL_MINUTES, V2_AGENTS_CONCURRENT_CALLS] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    api: {
      title: "tiers.pro.title",
      description: "tiers.pro.description_api",
      features_summary_label: "tiers.pro.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.pro.api.features.higher_quality_audio_192_kbps" },
      ] as PricingCardFeatureItem[],
      credits_usable: [
        { usable_for_translation_key: "credits_usable_for_high_quality_text_to_speech" },
        { usable_for_translation_key: "credits_usable_for_conversational_ai" },
      ],
    },
  },
  scale: {
    simple: {
      title: "tiers.scale.title",
      description: "tiers.scale.description",
      features_summary_label: "tiers.scale.simple.features_summary_label",
      features: [{ feature_name_translation_key: "tiers.scale.simple.features.workspace_seats" }],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.scale.title",
      description: "tiers.scale.description",
      features_summary_label: "tiers.scale.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.scale.creative_platform.features.team_collaboration" },
        { feature_name_translation_key: "tiers.scale.creative_platform.features.multi_seat_support" },
        V2_PROFESSIONAL_VOICE_CLONES,
      ] as PricingCardFeatureItem[],
      credits_usable: [
        { usable_for_translation_key: "tiers.scale.creative_platform.credits_usable.text_to_speech_minutes" },
        { usable_for_translation_key: "tiers.scale.creative_platform.credits_usable.music_minutes" },
        { usable_for_translation_key: "tiers.scale.creative_platform.credits_usable.sound_effects_seconds" },
      ],
    },
    agents_platform: {
      title: "tiers.scale.title",
      description: "tiers.scale.description_agents",
      features_summary_label: "tiers.scale.features_summary_label",
      features: [
        V2_AGENTS_CALL_MINUTES,
        V2_AGENTS_CONCURRENT_CALLS,
        { feature_name_translation_key: "tiers.scale.agents_platform.features.workspace_seats" },
      ] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    api: {
      title: "tiers.scale.title",
      description: "tiers.scale.description_api",
      features_summary_label: "tiers.scale.features_summary_label",
      features: [{ feature_name_translation_key: "tiers.scale.api.features.multi_seat_workspace" }],
      credits_usable: [
        { usable_for_translation_key: "credits_usable_for_high_quality_text_to_speech" },
        { usable_for_translation_key: "credits_usable_for_conversational_ai" },
      ],
    },
  },
  scale_2024_08_10: {
    simple: {
      title: "tiers.scale_2024_08_10.title",
      description: "tiers.scale_2024_08_10.description",
      features_summary_label: "tiers.scale_2024_08_10.simple.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.scale_2024_08_10.simple.features.low_latency_tts" },
        V2_PROFESSIONAL_VOICE_CLONES,
        { feature_name_translation_key: "tiers.scale_2024_08_10.simple.features.workspace_seats" },
      ] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.scale_2024_08_10.title",
      description: "tiers.scale_2024_08_10.description",
      features_summary_label: "tiers.scale_2024_08_10.features_summary_label",
      features: [
        {
          feature_name_translation_key:
            "tiers.scale_2024_08_10.creative_platform.features.low_latency_tts_as_low_as_5c_minute",
        },
        V2_PROFESSIONAL_VOICE_CLONES,
        V2_WORKSPACE_SEATS,
      ] as PricingCardFeatureItem[],
      credits_usable: [
        {
          usable_for_translation_key: "tiers.scale_2024_08_10.creative_platform.credits_usable.text_to_speech_minutes",
        },
        { usable_for_translation_key: "tiers.scale_2024_08_10.creative_platform.credits_usable.music_minutes" },
        { usable_for_translation_key: "tiers.scale_2024_08_10.creative_platform.credits_usable.sound_effects_seconds" },
      ],
    },
    agents_platform: {
      title: "tiers.scale_2024_08_10.title",
      description: "tiers.scale_2024_08_10.description_agents",
      features_summary_label: "tiers.scale_2024_08_10.features_summary_label",
      features: [
        V2_AGENTS_CALL_MINUTES,
        V2_AGENTS_CONCURRENT_CALLS,
        V2_WORKSPACE_SEATS,
        V2_PROFESSIONAL_VOICE_CLONES,
      ] as PricingCardFeatureItem[],
      credits_usable: [],
    },
    api: {
      title: "tiers.scale_2024_08_10.title",
      description: "tiers.scale_2024_08_10.description_api",
      features_summary_label: "tiers.scale_2024_08_10.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.scale_2024_08_10.api.features.low_latency_tts_as_low_as_5c_minute" },
        V2_PROFESSIONAL_VOICE_CLONES,
      ] as PricingCardFeatureItem[],
      credits_usable: [
        { usable_for_translation_key: "credits_usable_for_high_quality_text_to_speech" },
        { usable_for_translation_key: "credits_usable_for_conversational_ai" },
      ],
    },
  },
  enterprise: {
    simple: {
      title: "tiers.enterprise.title",
      description: "tiers.enterprise.description",
      features_summary_label: "tiers.enterprise.simple.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.enterprise.simple.features.custom_terms_dpa_slas" },
        { feature_name_translation_key: "tiers.enterprise.simple.features.baas_for_hipaa_customers" },
        { feature_name_translation_key: "tiers.enterprise.simple.features.custom_sso" },
        { feature_name_translation_key: "tiers.enterprise.simple.features.more_seats_and_voices" },
        { feature_name_translation_key: "tiers.enterprise.simple.features.elevated_concurrency_limits" },
        { feature_name_translation_key: "tiers.enterprise.simple.features.elevenstudios_fully_managed_dubbing" },
        { feature_name_translation_key: "tiers.enterprise.simple.features.priority_support" },
      ],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.enterprise.title",
      description: "tiers.enterprise.description",
      features_summary_label: "tiers.enterprise.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.enterprise.creative_platform.features.custom_terms_dpa_slas" },
        { feature_name_translation_key: "tiers.enterprise.creative_platform.features.baas_for_hipaa_customers" },
        { feature_name_translation_key: "tiers.enterprise.creative_platform.features.custom_sso" },
        { feature_name_translation_key: "tiers.enterprise.creative_platform.features.more_seats_and_voices" },
        { feature_name_translation_key: "tiers.enterprise.creative_platform.features.elevated_concurrency_limits" },
        {
          feature_name_translation_key:
            "tiers.enterprise.creative_platform.features.elevenstudios_fully_managed_dubbing",
        },
        { feature_name_translation_key: "tiers.enterprise.creative_platform.features.significant_discounts_at_scale" },
        { feature_name_translation_key: "tiers.enterprise.creative_platform.features.priority_support" },
      ],
      credits_usable: [{ usable_for_translation_key: "as_much_as_you_need" }],
    },
    agents_platform: {
      title: "tiers.enterprise.title",
      description: "tiers.enterprise.description_agents",
      features_summary_label: "tiers.enterprise.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.enterprise.agents_platform.features.elevated_concurrency_limits" },
        { feature_name_translation_key: "tiers.enterprise.agents_platform.features.custom_terms_dpa_slas" },
        { feature_name_translation_key: "tiers.enterprise.agents_platform.features.baas_for_hipaa_customers" },
        { feature_name_translation_key: "tiers.enterprise.agents_platform.features.custom_sso" },
        { feature_name_translation_key: "tiers.enterprise.agents_platform.features.more_seats_and_voices" },
        { feature_name_translation_key: "tiers.enterprise.agents_platform.features.significant_discounts_at_scale" },
        { feature_name_translation_key: "tiers.enterprise.agents_platform.features.priority_support" },
      ],
      credits_usable: [],
    },
    api: {
      title: "tiers.enterprise.title",
      description: "tiers.enterprise.description_api",
      features_summary_label: "tiers.enterprise.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.enterprise.api.features.custom_terms_dpa_slas" },
        { feature_name_translation_key: "tiers.enterprise.api.features.baas_for_hipaa_customers" },
        { feature_name_translation_key: "tiers.enterprise.api.features.custom_sso" },
        { feature_name_translation_key: "tiers.enterprise.api.features.more_seats_and_voices" },
        { feature_name_translation_key: "tiers.enterprise.api.features.elevated_concurrency_limits" },
        { feature_name_translation_key: "tiers.enterprise.api.features.elevenstudios_fully_managed_dubbing" },
        { feature_name_translation_key: "tiers.enterprise.api.features.significant_discounts_at_scale" },
        { feature_name_translation_key: "tiers.enterprise.api.features.priority_support" },
      ],
      credits_usable: [],
    },
  },
  payg: {
    simple: {
      title: "tiers.payg.title",
      description: "tiers.payg.description",
      features_summary_label: "tiers.payg.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.payg.simple.features.generated_speech_flash" },
        { feature_name_translation_key: "tiers.payg.simple.features.generated_speech_multilingual" },
        { feature_name_translation_key: "tiers.payg.simple.features.transcribed_speech_scribe" },
        { feature_name_translation_key: "tiers.payg.simple.features.live_transcribed_speech_scribe_realtime" },
        { feature_name_translation_key: "tiers.payg.simple.features.generated_music" },
        { feature_name_translation_key: "tiers.payg.simple.features.inbound_agent_calls" },
        { feature_name_translation_key: "tiers.payg.simple.features.outbound_agent_calls" },
      ],
      credits_usable: [],
    },
    creative_platform: {
      title: "tiers.payg.title",
      description: "tiers.payg.description",
      features_summary_label: "tiers.payg.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.payg.simple.features.generated_speech_flash" },
        { feature_name_translation_key: "tiers.payg.simple.features.generated_speech_multilingual" },
        { feature_name_translation_key: "tiers.payg.simple.features.transcribed_speech_scribe" },
        { feature_name_translation_key: "tiers.payg.simple.features.generated_music" },
      ],
      credits_usable: [],
    },
    agents_platform: {
      title: "tiers.payg.title",
      description: "tiers.payg.description",
      features_summary_label: "tiers.payg.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.payg.simple.features.inbound_agent_calls" },
        { feature_name_translation_key: "tiers.payg.simple.features.outbound_agent_calls" },
      ],
      credits_usable: [],
    },
    api: {
      title: "tiers.payg.title",
      description: "tiers.payg.description",
      features_summary_label: "tiers.payg.features_summary_label",
      features: [
        { feature_name_translation_key: "tiers.payg.simple.features.generated_speech_flash" },
        { feature_name_translation_key: "tiers.payg.simple.features.generated_speech_multilingual" },
        { feature_name_translation_key: "tiers.payg.simple.features.transcribed_speech_scribe" },
        { feature_name_translation_key: "tiers.payg.simple.features.live_transcribed_speech_scribe_realtime" },
        { feature_name_translation_key: "tiers.payg.simple.features.generated_music" },
        { feature_name_translation_key: "tiers.payg.simple.features.inbound_agent_calls" },
        { feature_name_translation_key: "tiers.payg.simple.features.outbound_agent_calls" },
      ],
      credits_usable: [],
    },
  },
} as const;

// Infer types from the const definition
export type PricingCardContent = (typeof PRICING_CARD_CONTENT)[keyof typeof PRICING_CARD_CONTENT];

const SIGN_UP_REDIRECT = `https://elevenlabs.io/app/sign-up?redirect=${encodeURIComponent("/app/subscription")}`;
const CONTACT_SALES_URL = "https://elevenlabs.io/contact-sales";

// --- Platform Config Shared Constants ---

/**
 * API platform rates are identical across all tiers.
 */
const SHARED_API_RATES: ApiPlatformConfig = {
  textToSpeech: {
    turboUsdPer1000Chars: 0.05,
    multilingualUsdPer1000Chars: 0.1,
  },
  speechToText: {
    scribeUsdPerHour: 0.22,
    scribeRealtimeUsdPerHour: 0.39,
    entityDetectionUsdPerHour: 0.07,
    keytermPromptingUsdPerHour: 0.05,
  },
  music: { usdPerMinute: 0.3 },
  voiceIsolator: { usdPerMinute: 0.12 },
  voiceChanger: { usdPerMinute: 0.12 },
  soundEffects: { usdPerMinute: 0.12 },
  dubbing: {
    autoUsdPerMinute: 0.33,
    autoNoWatermarkUsdPerMinute: 0.5,
    studioUsdPerMinute: 0.5,
  },
};

/**
 * Default Creative credit rates shared by all paid tiers.
 * All four dubbing modes available. Tiers that lack a feature set the relevant field to undefined.
 */
const DEFAULT_CREATIVE_RATES: CreativePlatformConfig = {
  textToSpeech: {
    nonTurboCreditsPerThousandChars: 1_000,
    turboCreditsPerThousandChars: 1_000,
  },
  speechToText: { creditsPerMinute: 330 },
  voiceChanger: { creditsPerMinute: 1_000 },
  voiceIsolator: { creditsPerMinute: 1_000 },
  soundEffects: { creditsPerGeneration: 200 },
  music: { creditsPerMinute: 1_500 },
  dubbing: {
    autoWatermarkCreditsPerMinute: 2_000,
    autoNoWatermarkCreditsPerMinute: 3_000,
    studioWatermarkCreditsPerMinute: 5_000,
    studioNoWatermarkCreditsPerMinute: 10_000,
  },
};

/**
 * Creative rates for tiers where no-watermark dubbing is not available
 * (payg, free, starter — dubbing noWatermark feature is disabled).
 */
const CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE: CreativePlatformConfig = {
  ...DEFAULT_CREATIVE_RATES,
  dubbing: {
    autoWatermarkCreditsPerMinute: 2_000,
    autoNoWatermarkCreditsPerMinute: undefined,
    studioWatermarkCreditsPerMinute: 5_000,
    studioNoWatermarkCreditsPerMinute: undefined,
  },
};

export const TIERS: Record<TierId, Tier> = {
  payg: {
    planId: "payg",
    type: "individual",
    pricing: {
      subscription: undefined,
      includedCredits: 10000,
      additionalCreditsPerThousand: { monthly: 0.12 },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: 10,
          turboMinutes: 20,
          characterLimit: 10000,
          concurrencyLimit: 2,
          priority: 3,
        },
        pricing: {
          overagePerThousandHighQuality: 0.12,
          overagePerThousandTurbo: 0.06,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: 8.3 },
        pricing: { pricePerMinuteOverride: 0.12 },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: 4.5, uiHours: 0.2 },
        pricing: {
          apiPricePerHour: 0.22,
          apiAdditionalPricePerHour: 0.22,
          uiAdditionalPricePerHour: undefined,
        },
      },
      speechToTextRealtime: {
        enabled: true,
        limits: { includedHours: 2.5 },
        pricing: { pricePerHour: 0.39, additionalPricePerHour: 0.39 },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: 15, concurrencyLimit: 4 },
        pricing: { hostingCostPerMinute: 0.07, extraMinuteCost: 0.07, textMessageCost: 0.01 },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: false, studio: false },
        limits: {
          automaticWatermarkMinutes: 2.53,
          automaticNoWatermarkMinutes: 0,
          studioWatermarkMinutes: 0,
          studioNoWatermarkMinutes: 0,
        },
        pricing: {
          pricePerMinuteWithWatermark: 0.396,
          additionalMinuteWithWatermark: 0.396,
          pricePerMinuteWithoutWatermark: 0.5928,
          additionalMinuteWithoutWatermark: 0.5928,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: 250, includedGenerations: 8 },
        pricing: { pricePerMinuteOverride: 0.1188 },
      },
      music: {
        enabled: true,
        hasOveragePricing: true,
        features: { commercialUse: false },
        limits: { includedMinutes: 3 },
        pricing: { pricePerMinute: 0.325 },
      },
      voiceIsolator: {
        enabled: true,
        hasOveragePricing: true,
        limits: { includedMinutes: 8.3 },
        pricing: { pricePerMinuteOverride: 0.12 },
      },
      voiceChanger: {
        enabled: true,
        hasOveragePricing: true,
        limits: { includedMinutes: 8.3 },
        pricing: { pricePerMinuteOverride: 0.12 },
      },
      projects: {
        enabled: true,
        limits: { maxProjects: 3 },
      },
      audioNative: {
        enabled: false,
        limits: { maxEmbeds: 0 },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: 40,
          videoSeconds: 0,
          upscalingImages: 0,
          upscalingVideoSeconds: 0,
          lipsyncSeconds: 0,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: false, professionalVoiceCloning: false },
        limits: { pvcSlots: 0, customVoices: 3 },
      },
    },
    capabilities: {
      commercialLicense: false,
      noAttributionRequired: false,
      usageAnalytics: true,
      multipleSeats: false,
    },
    api: {
      formats: "16kHz PCM, uLaw",
    },
    cta: { href: SIGN_UP_REDIRECT },
    display: {
      titleKey: "tiers.payg.title",
      descriptionKey: "tiers.payg.description",
      featuresSummaryLabelKey: "tiers.payg.features_summary_label",
    },
    platformConfig: {
      creative: {
        ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE,
        textToSpeech: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.textToSpeech,
          overrideIncludedMultilingualMinutes: 10,
          overrideIncludedTurboMinutes: 10,
        },
        speechToText: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.speechToText,
          overrideIncludedHours: 0.2,
        },
        voiceChanger: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.voiceChanger,
          overrideIncludedMinutes: 8.3,
        },
        voiceIsolator: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.voiceIsolator,
          overrideIncludedMinutes: 8.3,
        },
        soundEffects: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.soundEffects,
          overrideIncludedGenerations: 8,
        },
        music: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.music,
          overrideIncludedMinutes: 3,
        },
        dubbing: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.dubbing,
          overrideIncludedAutoWatermarkMinutes: 2.53,
        },
      },
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0.08,
        audioBurstUsdPerMinute: 0.08,
        textMessageUsdPerMessage: 0.003,
        includedMinutes: 15,
        concurrencyLimit: 4,
      },
    },
  },
  free: {
    planId: "free",
    type: "individual",
    pricing: {
      subscription: {
        monthly: { price: 0 },
        yearly: { price: 0 },
      },
      includedCredits: 10000,
      additionalCreditsPerThousand: { monthly: undefined },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: 10,
          turboMinutes: 20,
          characterLimit: 10000,
          concurrencyLimit: 2,
          priority: 3,
        },
        pricing: {
          overagePerThousandHighQuality: undefined,
          overagePerThousandTurbo: undefined,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: 10 },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: 2.5, uiHours: 0.2 },
        pricing: {
          apiPricePerHour: undefined,
          apiAdditionalPricePerHour: undefined,
          uiAdditionalPricePerHour: undefined,
        },
      },
      speechToTextRealtime: {
        enabled: false,
        limits: { includedHours: 0 },
        pricing: { pricePerHour: undefined, additionalPricePerHour: undefined },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: 15, concurrencyLimit: 4 },
        pricing: { hostingCostPerMinute: undefined, extraMinuteCost: undefined, textMessageCost: undefined },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: false, studio: false },
        limits: {
          automaticWatermarkMinutes: 5,
          automaticNoWatermarkMinutes: 0,
          studioWatermarkMinutes: 0,
          studioNoWatermarkMinutes: 0,
        },
        pricing: {
          pricePerMinuteWithWatermark: 0,
          additionalMinuteWithWatermark: undefined,
          pricePerMinuteWithoutWatermark: undefined,
          additionalMinuteWithoutWatermark: undefined,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: 250, includedGenerations: 50 },
      },
      music: {
        enabled: true,
        features: { commercialUse: false },
        limits: { includedMinutes: 11 },
        pricing: { pricePerMinute: undefined },
      },
      voiceIsolator: {
        enabled: true,
        limits: { includedMinutes: 10 },
      },
      voiceChanger: {
        enabled: true,
        limits: { includedMinutes: 10 },
      },
      projects: {
        enabled: true,
        limits: { maxProjects: 3 },
      },
      audioNative: {
        enabled: false,
        limits: { maxEmbeds: 0 },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: 40,
          videoSeconds: 0,
          upscalingImages: 0,
          upscalingVideoSeconds: 0,
          lipsyncSeconds: 0,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: false, professionalVoiceCloning: false },
        limits: { pvcSlots: 0, customVoices: 3 },
      },
      productions: {
        enabled: true,
        pricing: {
          transcriptsPerMinute: 2,
          captionsPerMinute: 2.2,
          subtitlesPerMinute: 7,
          dubsPerMinute: 22,
          audiobooksPerHour: 200,
        },
      },
    },
    capabilities: {
      commercialLicense: false,
      noAttributionRequired: false,
      usageAnalytics: true,
      multipleSeats: false,
    },
    api: {
      formats: "16kHz PCM, uLaw",
    },
    cta: { href: SIGN_UP_REDIRECT },
    display: {
      titleKey: "tiers.free.title",
      descriptionKey: "tiers.free.description",
      featuresSummaryLabelKey: "tiers.free.features_summary_label",
    },
    platformConfig: {
      creative: {
        ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE,
        textToSpeech: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.textToSpeech,
          overrideIncludedMultilingualMinutes: 10,
          overrideIncludedTurboMinutes: 10,
        },
        speechToText: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.speechToText,
          overrideIncludedHours: 0.2,
        },
        voiceChanger: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.voiceChanger,
          overrideIncludedMinutes: 10,
        },
        voiceIsolator: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.voiceIsolator,
          overrideIncludedMinutes: 10,
        },
        soundEffects: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.soundEffects,
          overrideIncludedGenerations: 50,
        },
        music: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.music,
          overrideIncludedMinutes: 11,
        },
        dubbing: {
          ...CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE.dubbing,
          overrideIncludedAutoWatermarkMinutes: 5,
          overrideIncludedStudioWatermarkMinutes: 0,
        },
      },
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0.08,
        audioBurstUsdPerMinute: 0.08,
        textMessageUsdPerMessage: 0.003,
        includedMinutes: 15,
        concurrencyLimit: 4,
      },
    },
  },

  starter: {
    planId: "starter",
    type: "individual",
    pricing: {
      subscription: {
        monthly: { price: 6 },
        yearly: { price: 6, discountedPrice: 5, discountMessageKey: "two_months_free" },
      },
      includedCredits: 30000,
      additionalCreditsPerThousand: { monthly: undefined },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: 30,
          turboMinutes: 60,
          characterLimit: 30000,
          concurrencyLimit: 3,
          priority: 4,
        },
        pricing: {
          overagePerThousandHighQuality: undefined,
          overagePerThousandTurbo: undefined,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: 30 },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: 12.5, uiHours: 1 },
        pricing: {
          apiPricePerHour: 0.4,
          apiAdditionalPricePerHour: undefined,
          uiAdditionalPricePerHour: undefined,
          keytermPromptingBaseMultiplier: 0.2,
          entityDetectionBaseMultiplier: 0.3,
        },
      },
      speechToTextRealtime: {
        enabled: true,
        limits: { includedHours: 10 },
        pricing: { pricePerHour: 0.48, additionalPricePerHour: undefined },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: 50, concurrencyLimit: 6 },
        pricing: { hostingCostPerMinute: 0.1, extraMinuteCost: undefined, textMessageCost: 0.004 },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: false, studio: true },
        limits: {
          automaticWatermarkMinutes: 15,
          automaticNoWatermarkMinutes: 0,
          studioWatermarkMinutes: 6,
          studioNoWatermarkMinutes: 0,
        },
        pricing: {
          pricePerMinuteWithWatermark: 0.33,
          additionalMinuteWithWatermark: undefined,
          pricePerMinuteWithoutWatermark: undefined,
          additionalMinuteWithoutWatermark: undefined,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: 750, includedGenerations: 150 },
      },
      music: {
        enabled: true,
        features: { commercialUse: "excluding_enterprise_and_streaming" },
        limits: { includedMinutes: 16, concurrencyLimit: 2, priority: 4 },
        pricing: { pricePerMinute: 0.3 },
      },
      voiceIsolator: {
        enabled: true,
        limits: { includedMinutes: 30 },
      },
      voiceChanger: {
        enabled: true,
        limits: { includedMinutes: 30 },
      },
      projects: {
        enabled: true,
        limits: { maxProjects: 20 },
      },
      audioNative: {
        enabled: false,
        limits: { maxEmbeds: 0 },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: 198,
          videoSeconds: 211,
          upscalingImages: 198,
          upscalingVideoSeconds: 49,
          lipsyncSeconds: 742,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: true, professionalVoiceCloning: false },
        limits: { pvcSlots: 0, customVoices: 10 },
      },
      productions: {
        enabled: true,
        pricing: {
          transcriptsPerMinute: 2,
          captionsPerMinute: 2.2,
          subtitlesPerMinute: 7,
          dubsPerMinute: 22,
          audiobooksPerHour: 200,
        },
      },
    },
    capabilities: {
      commercialLicense: true,
      noAttributionRequired: true,
      usageAnalytics: true,
      multipleSeats: false,
    },
    api: {
      formats: "22.05kHz PCM, uLaw",
    },
    cta: { href: SIGN_UP_REDIRECT },
    display: {
      titleKey: "tiers.starter.title",
      descriptionKey: "tiers.starter.description",
      featuresSummaryLabelKey: "tiers.starter.features_summary_label",
    },
    platformConfig: {
      creative: CREATIVE_RATES_NO_WATERMARK_UNAVAILABLE,
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0.08,
        audioBurstUsdPerMinute: 0.08,
        textMessageUsdPerMessage: 0.003,
        includedMinutes: 50,
        concurrencyLimit: 6,
      },
    },
  },

  creator: {
    planId: "creator",
    type: "individual",
    pricing: {
      subscription: {
        monthly: { price: 22, discountedPrice: 11, discountMessageKey: "first_month_half_price" },
        yearly: { price: 22, discountedPrice: 18.33, discountMessageKey: "two_months_free" },
      },
      includedCredits: 121_000,
      additionalCreditsPerThousand: { monthly: 0.3 },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: 121,
          turboMinutes: 242,
          characterLimit: 121_000,
          concurrencyLimit: 5,
          priority: 5,
        },
        pricing: {
          overagePerThousandHighQuality: 0.3,
          overagePerThousandTurbo: 0.15,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: 121 },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: 62.85, uiHours: 4.88 },
        pricing: {
          apiPricePerHour: 0.35,
          apiAdditionalPricePerHour: 0.48,
          uiAdditionalPricePerHour: 4.5,
          keytermPromptingBaseMultiplier: 0.2,
          entityDetectionBaseMultiplier: 0.3,
        },
      },
      speechToTextRealtime: {
        enabled: true,
        limits: { includedHours: 48 },
        pricing: { pricePerHour: 0.46, additionalPricePerHour: 0.63 },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: 250, concurrencyLimit: 10 },
        pricing: { hostingCostPerMinute: 0.088, extraMinuteCost: 0.12, textMessageCost: 0.004 },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: true, studio: true },
        limits: {
          automaticWatermarkMinutes: 50,
          automaticNoWatermarkMinutes: 33,
          studioWatermarkMinutes: 20,
          studioNoWatermarkMinutes: 10,
        },
        pricing: {
          pricePerMinuteWithWatermark: 0.44,
          additionalMinuteWithWatermark: 0.6,
          pricePerMinuteWithoutWatermark: 0.67,
          additionalMinuteWithoutWatermark: 0.9,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: 3_025, includedGenerations: 605 },
        hasOveragePricing: true,
      },
      music: {
        enabled: true,
        features: { commercialUse: "excluding_enterprise" },
        limits: { includedMinutes: 62, concurrencyLimit: 2, priority: 5 },
        pricing: { pricePerMinute: 0.35 },
        hasOveragePricing: true,
      },
      voiceIsolator: {
        enabled: true,
        limits: { includedMinutes: 121 },
        hasOveragePricing: true,
      },
      voiceChanger: {
        enabled: true,
        limits: { includedMinutes: 121 },
        hasOveragePricing: true,
      },
      projects: {
        enabled: true,
        limits: { maxProjects: 1000 },
      },
      audioNative: {
        enabled: true,
        limits: { maxEmbeds: 1000 },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: 660,
          videoSeconds: 705,
          upscalingImages: 660,
          upscalingVideoSeconds: 165,
          lipsyncSeconds: 2475,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: true, professionalVoiceCloning: true },
        limits: { pvcSlots: 1, customVoices: 30 },
      },
      productions: {
        enabled: true,
        pricing: {
          transcriptsPerMinute: 2,
          captionsPerMinute: 2.2,
          subtitlesPerMinute: 7,
          dubsPerMinute: 22,
          audiobooksPerHour: 200,
        },
      },
    },
    capabilities: {
      commercialLicense: true,
      noAttributionRequired: true,
      usageAnalytics: true,
      multipleSeats: false,
    },
    api: {
      formats: "24kHz PCM, uLaw",
    },
    cta: { href: SIGN_UP_REDIRECT },
    display: {
      titleKey: "tiers.creator.title",
      descriptionKey: "tiers.creator.description",
      featuresSummaryLabelKey: "tiers.creator.features_summary_label",
    },
    platformConfig: {
      creative: DEFAULT_CREATIVE_RATES,
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0.08,
        audioBurstUsdPerMinute: 0.08,
        textMessageUsdPerMessage: 0.003,
        includedMinutes: 250,
        concurrencyLimit: 10,
      },
    },
  },

  pro: {
    planId: "pro",
    type: "individual",
    pricing: {
      subscription: {
        monthly: { price: 99 },
        yearly: { price: 99, discountedPrice: 82.5, discountMessageKey: "two_months_free" },
      },
      includedCredits: 600_000,
      additionalCreditsPerThousand: { monthly: 0.24 },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: 600,
          turboMinutes: 1_200,
          characterLimit: 600_000,
          concurrencyLimit: 10,
          priority: 5,
        },
        pricing: {
          overagePerThousandHighQuality: 0.24,
          overagePerThousandTurbo: 0.12,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: 600 },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: 300, uiHours: 24.75 },
        pricing: {
          apiPricePerHour: 0.33,
          apiAdditionalPricePerHour: 0.4,
          uiAdditionalPricePerHour: 4,
          keytermPromptingBaseMultiplier: 0.2,
          entityDetectionBaseMultiplier: 0.3,
        },
      },
      speechToTextRealtime: {
        enabled: true,
        limits: { includedHours: 225 },
        pricing: { pricePerHour: 0.44, additionalPricePerHour: 0.53 },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: 1100, concurrencyLimit: 20 },
        pricing: { hostingCostPerMinute: 0.09, extraMinuteCost: 0.11, textMessageCost: 0.003 },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: true, studio: true },
        limits: {
          automaticWatermarkMinutes: 250,
          automaticNoWatermarkMinutes: 167,
          studioWatermarkMinutes: 100,
          studioNoWatermarkMinutes: 50,
        },
        pricing: {
          pricePerMinuteWithWatermark: 0.4,
          additionalMinuteWithWatermark: 0.48,
          pricePerMinuteWithoutWatermark: 0.59,
          additionalMinuteWithoutWatermark: 0.72,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: 15_000, includedGenerations: 3_000 },
        hasOveragePricing: true,
      },
      music: {
        enabled: true,
        features: { commercialUse: "excluding_enterprise" },
        limits: { includedMinutes: 304, concurrencyLimit: 2, priority: 5 },
        pricing: { pricePerMinute: 0.325 },
        hasOveragePricing: true,
      },
      voiceIsolator: {
        enabled: true,
        limits: { includedMinutes: 600 },
        hasOveragePricing: true,
      },
      voiceChanger: {
        enabled: true,
        limits: { includedMinutes: 600 },
        hasOveragePricing: true,
      },
      projects: {
        enabled: true,
        limits: { maxProjects: 3000 },
      },
      audioNative: {
        enabled: true,
        limits: { maxEmbeds: 3000 },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: 3300,
          videoSeconds: 3525,
          upscalingImages: 3300,
          upscalingVideoSeconds: 825,
          lipsyncSeconds: 12375,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: true, professionalVoiceCloning: true },
        limits: { pvcSlots: 1, customVoices: 160 },
      },
      productions: {
        enabled: true,
        pricing: {
          transcriptsPerMinute: 2,
          captionsPerMinute: 2.2,
          subtitlesPerMinute: 7,
          dubsPerMinute: 22,
          audiobooksPerHour: 200,
        },
      },
    },
    capabilities: {
      commercialLicense: true,
      noAttributionRequired: true,
      usageAnalytics: true,
      multipleSeats: false,
    },
    api: {
      formats: "44.1kHz PCM, uLaw",
    },
    cta: { href: SIGN_UP_REDIRECT },
    display: {
      titleKey: "tiers.pro.title",
      descriptionKey: "tiers.pro.description",
      featuresSummaryLabelKey: "tiers.pro.features_summary_label",
    },
    platformConfig: {
      creative: DEFAULT_CREATIVE_RATES,
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0.08,
        audioBurstUsdPerMinute: 0.08,
        textMessageUsdPerMessage: 0.003,
        includedMinutes: 1_100,
        concurrencyLimit: 20,
      },
    },
  },

  scale: {
    planId: "scale",
    type: "company",
    pricing: {
      subscription: {
        monthly: { price: 299 },
        yearly: { price: 299, discountedPrice: 249.17, discountMessageKey: "two_months_free" },
      },
      includedCredits: 1_800_000,
      additionalCreditsPerThousand: { monthly: 0.18 },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: 1_800,
          turboMinutes: 3_600,
          characterLimit: 1_800_000,
          concurrencyLimit: 15,
          priority: 5,
        },
        pricing: {
          overagePerThousandHighQuality: 0.18,
          overagePerThousandTurbo: 0.09,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: 1_800 },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: 1100, uiHours: 94.28 },
        pricing: {
          apiPricePerHour: 0.3,
          apiAdditionalPricePerHour: 0.33,
          uiAdditionalPricePerHour: 3.5,
          keytermPromptingBaseMultiplier: 0.2,
          entityDetectionBaseMultiplier: 0.3,
        },
      },
      speechToTextRealtime: {
        enabled: true,
        limits: { includedHours: 786 },
        pricing: { pricePerHour: 0.42, additionalPricePerHour: 0.46 },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: 3600, concurrencyLimit: 30 },
        pricing: { hostingCostPerMinute: 0.09, extraMinuteCost: 0.1, textMessageCost: 0.003 },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: true, studio: true },
        limits: {
          automaticWatermarkMinutes: 1000,
          automaticNoWatermarkMinutes: 667,
          studioWatermarkMinutes: 400,
          studioNoWatermarkMinutes: 200,
        },
        pricing: {
          pricePerMinuteWithWatermark: 0.33,
          additionalMinuteWithWatermark: 0.36,
          pricePerMinuteWithoutWatermark: 0.49,
          additionalMinuteWithoutWatermark: 0.54,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: 45_000, includedGenerations: 9_000 },
        hasOveragePricing: true,
      },
      music: {
        enabled: true,
        features: { commercialUse: "excluding_enterprise" },
        limits: { includedMinutes: 1100, concurrencyLimit: 3, priority: 5 },
        pricing: { pricePerMinute: 0.3 },
        hasOveragePricing: true,
      },
      voiceIsolator: {
        enabled: true,
        limits: { includedMinutes: 1_800 },
        hasOveragePricing: true,
      },
      voiceChanger: {
        enabled: true,
        limits: { includedMinutes: 1_800 },
        hasOveragePricing: true,
      },
      projects: {
        enabled: true,
        limits: { maxProjects: 9_000 },
      },
      audioNative: {
        enabled: true,
        limits: { maxEmbeds: 20000 },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: 11000,
          videoSeconds: 11752,
          upscalingImages: 11000,
          upscalingVideoSeconds: 2750,
          lipsyncSeconds: 41250,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: true, professionalVoiceCloning: true },
        limits: { pvcSlots: 3, customVoices: 660 },
      },
      productions: {
        enabled: true,
        pricing: {
          transcriptsPerMinute: 2,
          captionsPerMinute: 2.2,
          subtitlesPerMinute: 7,
          dubsPerMinute: 22,
          audiobooksPerHour: 200,
        },
      },
    },
    capabilities: {
      commercialLicense: true,
      noAttributionRequired: true,
      usageAnalytics: true,
      multipleSeats: true,
      seats: 3,
    },
    api: {
      formats: "44.1kHz PCM, uLaw",
    },
    cta: { href: SIGN_UP_REDIRECT },
    display: {
      titleKey: "tiers.scale.title",
      descriptionKey: "tiers.scale.description",
      featuresSummaryLabelKey: "tiers.scale.features_summary_label",
    },
    platformConfig: {
      creative: DEFAULT_CREATIVE_RATES,
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0.08,
        audioBurstUsdPerMinute: 0.08,
        textMessageUsdPerMessage: 0.003,
        includedMinutes: 3_600,
        concurrencyLimit: 30,
      },
    },
  },

  scale_2024_08_10: {
    planId: "scale_2024_08_10",
    type: "company",
    pricing: {
      subscription: {
        monthly: { price: 990 },
        yearly: { price: 990, discountedPrice: 825, discountMessageKey: "two_months_free" },
      },
      includedCredits: 6_000_000,
      additionalCreditsPerThousand: { monthly: 0.12, annual: 0.1 },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: 6_000,
          turboMinutes: 12_000,
          characterLimit: 6_000_000,
          concurrencyLimit: 15,
          priority: 5,
        },
        pricing: {
          overagePerThousandHighQuality: 0.12,
          overagePerThousandTurbo: 0.06,
          overagePerThousandHighQualityAnnual: 0.1,
          overagePerThousandTurboAnnual: 0.05,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: 6_000 },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: 6000, uiHours: 440 },
        pricing: {
          apiPricePerHour: 0.22,
          apiAdditionalPricePerHour: 0.22,
          uiAdditionalPricePerHour: 3,
          keytermPromptingBaseMultiplier: 0.2,
          entityDetectionBaseMultiplier: 0.3,
        },
      },
      speechToTextRealtime: {
        enabled: true,
        limits: { includedHours: 3385 },
        pricing: { pricePerHour: 0.39, additionalPricePerHour: 0.39 },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: 13750, concurrencyLimit: 30 },
        pricing: {
          hostingCostPerMinute: 0.09,
          extraMinuteCost: 0.096,
          extraMinuteCostAnnual: 0.08,
          textMessageCost: 0.003,
        },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: true, studio: true },
        limits: {
          automaticWatermarkMinutes: 5500,
          automaticNoWatermarkMinutes: 3667,
          studioWatermarkMinutes: 2200,
          studioNoWatermarkMinutes: 1100,
        },
        pricing: {
          pricePerMinuteWithWatermark: 0.24,
          additionalMinuteWithWatermark: 0.24,
          pricePerMinuteWithoutWatermark: 0.36,
          additionalMinuteWithoutWatermark: 0.36,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: 150_000, includedGenerations: 30_000 },
        hasOveragePricing: true,
      },
      music: {
        enabled: true,
        features: { commercialUse: "excluding_enterprise" },
        limits: { includedMinutes: 4800, concurrencyLimit: 3, priority: 5 },
        pricing: { pricePerMinute: 0.275 },
        hasOveragePricing: true,
      },
      voiceIsolator: {
        enabled: true,
        limits: { includedMinutes: 6_000 },
        hasOveragePricing: true,
      },
      voiceChanger: {
        enabled: true,
        limits: { includedMinutes: 6_000 },
        hasOveragePricing: true,
      },
      projects: {
        enabled: true,
        limits: { maxProjects: 30_000 },
      },
      audioNative: {
        enabled: true,
        limits: { maxEmbeds: 20000 },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: 44000,
          videoSeconds: 47008,
          upscalingImages: 44000,
          upscalingVideoSeconds: 11000,
          lipsyncSeconds: 165000,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: true, professionalVoiceCloning: true },
        limits: { pvcSlots: 10, customVoices: 2_200 },
      },
      productions: {
        enabled: true,
        pricing: {
          transcriptsPerMinute: 2,
          captionsPerMinute: 2.2,
          subtitlesPerMinute: 7,
          dubsPerMinute: 22,
          audiobooksPerHour: 200,
        },
      },
    },
    capabilities: {
      commercialLicense: true,
      noAttributionRequired: true,
      usageAnalytics: true,
      multipleSeats: true,
      seats: 10,
    },
    api: {
      formats: "44.1kHz PCM, uLaw",
    },
    cta: { href: SIGN_UP_REDIRECT },
    display: {
      titleKey: "tiers.scale_2024_08_10.title",
      descriptionKey: "tiers.scale_2024_08_10.description",
      featuresSummaryLabelKey: "tiers.scale_2024_08_10.features_summary_label",
    },
    platformConfig: {
      creative: DEFAULT_CREATIVE_RATES,
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0.08,
        audioBurstUsdPerMinute: 0.08,
        textMessageUsdPerMessage: 0.003,
        includedMinutes: 13_750,
        concurrencyLimit: 40,
      },
    },
  },

  enterprise: {
    planId: "enterprise",
    type: "company",
    pricing: {
      subscription: undefined,
      includedCredits: Infinity,
      additionalCreditsPerThousand: { monthly: undefined },
    },
    products: {
      textToSpeech: {
        enabled: true,
        limits: {
          highQualityMinutes: Infinity,
          turboMinutes: Infinity,
          characterLimit: Infinity,
          concurrencyLimit: Infinity,
          priority: 6,
        },
        pricing: {
          overagePerThousandHighQuality: undefined,
          overagePerThousandTurbo: undefined,
        },
      },
      speechToSpeech: {
        enabled: true,
        limits: { includedMinutes: Infinity },
      },
      speechToText: {
        enabled: true,
        limits: { apiHours: Infinity, uiHours: Infinity },
        pricing: {
          apiPricePerHour: 0.12,
          apiAdditionalPricePerHour: 0.12,
          uiAdditionalPricePerHour: 2.5,
          keytermPromptingBaseMultiplier: 0.2,
          entityDetectionBaseMultiplier: 0.3,
        },
      },
      speechToTextRealtime: {
        enabled: true,
        limits: { includedHours: Infinity },
        pricing: { pricePerHour: 0.15, additionalPricePerHour: 0.15 },
      },
      conversationalAI: {
        enabled: true,
        limits: { includedMinutes: Infinity, concurrencyLimit: Infinity },
        pricing: { hostingCostPerMinute: undefined, extraMinuteCost: undefined, textMessageCost: undefined },
      },
      dubbing: {
        enabled: true,
        features: { noWatermark: true, studio: true },
        limits: {
          automaticWatermarkMinutes: Infinity,
          automaticNoWatermarkMinutes: Infinity,
          studioWatermarkMinutes: Infinity,
          studioNoWatermarkMinutes: Infinity,
        },
        pricing: {
          pricePerMinuteWithWatermark: undefined,
          additionalMinuteWithWatermark: undefined,
          pricePerMinuteWithoutWatermark: undefined,
          additionalMinuteWithoutWatermark: undefined,
        },
      },
      soundEffects: {
        enabled: true,
        limits: { includedSeconds: Infinity, includedGenerations: Infinity },
      },
      music: {
        enabled: true,
        features: { commercialUse: false },
        limits: { includedMinutes: 4800, concurrencyLimit: 3, priority: 5 },
        pricing: { pricePerMinute: undefined },
      },
      voiceIsolator: {
        enabled: true,
        limits: { includedMinutes: Infinity },
      },
      voiceChanger: {
        enabled: true,
        limits: { includedMinutes: Infinity },
      },
      projects: {
        enabled: true,
        limits: { maxProjects: Infinity },
      },
      audioNative: {
        enabled: true,
        limits: { maxEmbeds: Infinity },
      },
      imageVideo: {
        enabled: true,
        limits: {
          images: Infinity,
          videoSeconds: Infinity,
          upscalingImages: Infinity,
          upscalingVideoSeconds: Infinity,
          lipsyncSeconds: Infinity,
        },
      },
      voices: {
        enabled: true,
        features: { instantVoiceCloning: true, professionalVoiceCloning: true },
        limits: { pvcSlots: Infinity, customVoices: Infinity },
      },
      productions: {
        enabled: true,
        pricing: {
          transcriptsPerMinute: 2,
          captionsPerMinute: 2.2,
          subtitlesPerMinute: 7,
          dubsPerMinute: 22,
          audiobooksPerHour: 200,
        },
      },
    },
    capabilities: {
      commercialLicense: true,
      noAttributionRequired: true,
      usageAnalytics: true,
      multipleSeats: true,
    },
    api: {
      formats: "44.1kHz PCM, uLaw",
    },
    cta: { href: CONTACT_SALES_URL },
    display: {
      titleKey: "tiers.enterprise.title",
      descriptionKey: "tiers.enterprise.description",
      featuresSummaryLabelKey: "tiers.enterprise.features_summary_label",
    },
    platformConfig: {
      creative: DEFAULT_CREATIVE_RATES,
      api: SHARED_API_RATES,
      agents: {
        audioUsdPerMinute: 0,
        audioBurstUsdPerMinute: 0,
        textMessageUsdPerMessage: 0,
        includedMinutes: Infinity,
        concurrencyLimit: Infinity,
      },
    },
  },
};

export const PRICING_TIERS = Object.values(TIERS);

/** Returns the tier map. */
export function getTiers(): Record<TierId, Tier> {
  return TIERS;
}

/** Returns the tier array. */
export function getPricingTiers(): Tier[] {
  return PRICING_TIERS;
}

/** Returns the pricing card content. */
export function getPricingCardContent(): typeof PRICING_CARD_CONTENT {
  return PRICING_CARD_CONTENT;
}

// --- Pricing Calculation Utilities ---

export type BillingPeriod = "monthly" | "yearly";

/**
 * Calculates the dollar price per unit for a Creative platform product.
 * Formula: creditRatePerUnit × (subscriptionPrice / includedCredits)
 *
 * @param creditRatePerUnit - Credits consumed per unit (e.g. per char, per minute, per generation)
 * @param tier - The pricing tier
 * @param period - Billing period (monthly or yearly)
 * @returns Dollar price per unit, or undefined if the tier has no subscription or included credits
 */
export function getCreativeDollarPrice(
  creditRatePerUnit: number,
  tier: Tier,
  period: BillingPeriod,
): number | undefined {
  const subscriptionPrice = getSubscriptionPrice(tier, period);
  const includedCredits = tier.pricing.includedCredits;
  if (!subscriptionPrice || !includedCredits) return undefined;
  return (creditRatePerUnit * subscriptionPrice) / includedCredits;
}

/**
 * Calculates the number of included units for a Creative platform product.
 * Formula: includedCredits / creditRatePerUnit
 *
 * @param creditRatePerUnit - Credits consumed per unit (e.g. per char, per minute, per generation)
 * @param tier - The pricing tier
 * @returns Number of included units, or undefined if the tier has no included credits
 */
export function getCreativeIncludedUnits(creditRatePerUnit: number, tier: Tier): number | undefined {
  const includedCredits = tier.pricing.includedCredits;
  if (!includedCredits) return undefined;
  return includedCredits / creditRatePerUnit;
}

/** Get subscription price for a period (uses discountedPrice for yearly) */
export const getSubscriptionPrice = (tier: Tier, period: BillingPeriod): number | undefined => {
  const subscription = tier.pricing.subscription;
  if (!subscription) return undefined;
  return period === "yearly"
    ? (subscription.yearly?.discountedPrice ?? subscription.yearly?.price)
    : subscription.monthly?.price;
};

/** Calculate TTS price per 1k characters based on subscription price / included credits */
export const getTtsPricePerThousand = (
  tier: Tier,
  period: BillingPeriod,
  multiplier: number = 1,
): number | undefined => {
  const subscriptionPrice = getSubscriptionPrice(tier, period);
  const characterLimit = tier.products.textToSpeech.limits.characterLimit;

  if (tier.planId === "payg") {
    return (tier.products.textToSpeech.pricing.overagePerThousandHighQuality ?? 0) * multiplier;
  }

  if (subscriptionPrice === undefined || characterLimit === undefined || characterLimit === 0) {
    return undefined;
  }
  return (subscriptionPrice / characterLimit) * 1000 * multiplier;
};

/**
 * Calculate the overage multiplier based on TTS multilingual pricing.
 * This represents how much more expensive overage is compared to included pricing.
 * Formula: (overagePerThousandHighQuality / includedPricePerThousand) - 1
 */
export const getOverageMultiplier = (tier: Tier, period: BillingPeriod): number | undefined => {
  const includedPrice = getTtsPricePerThousand(tier, period);
  const overagePrice = tier.products.textToSpeech.pricing.overagePerThousandHighQuality;
  if (includedPrice === undefined || includedPrice === 0 || overagePrice === undefined) {
    return undefined;
  }
  return overagePrice / includedPrice - 1;
};

/**
 * Calculate overage price by applying the overage multiplier to an included price.
 * overagePrice = includedPrice * (1 + overageMultiplier)
 */
export const getOveragePrice = (
  includedPrice: number | undefined,
  tier: Tier,
  period: BillingPeriod,
): number | undefined => {
  if (tier.planId === "payg") {
    return includedPrice;
  }
  if (includedPrice === undefined || includedPrice === 0) return undefined;
  const multiplier = getOverageMultiplier(tier, period);
  if (multiplier === undefined) return undefined;
  return includedPrice * (1 + multiplier);
};

/** Calculate voice changer price per minute: subscriptionPrice / includedMinutes */
export const getVoiceChangerPricePerMinute = (tier: Tier, period: BillingPeriod): number | undefined => {
  const subscriptionPrice = getSubscriptionPrice(tier, period);
  const includedMinutes = tier.products.voiceChanger.limits.includedMinutes;
  if (subscriptionPrice === undefined || includedMinutes === undefined || includedMinutes === 0) {
    return undefined;
  }
  return subscriptionPrice / includedMinutes;
};

/** Apply annual discount (2 months free = 10/12) */
export const applyAnnualDiscount = (price: number | undefined, period: BillingPeriod): number | undefined => {
  if (price === undefined) return undefined;
  if (period === "yearly") {
    return price * (10 / 12);
  }
  return price;
};

function getMusicCreativeOverageUsdPerMinuteFromCredits(tier: Tier, creditsPerMinute: number) {
  if (tier.planId === "free") {
    const creatorTier = getTiers().creator;
    const creatorPrice = getCreativeDollarPrice(creditsPerMinute, creatorTier, "monthly");
    return creatorPrice !== undefined ? creatorPrice * 2 : undefined;
  }
  return getCreativeDollarPrice(creditsPerMinute, tier, "monthly");
}

/** Creative platform music credits consumed per minute */
export function getMusicCreativeCreditsPerMinute({ featureFlags }: PricingDataOptions = {}) {
  return featureFlags?.[PRICING_FEATURE_FLAGS.MUSIC_PRICING_V2]
    ? MUSIC_PRICING_V2.creativeCreditsPerMinute
    : MUSIC_PRICING_LEGACY.creativeCreditsPerMinute;
}

/** API platform music USD per minute */
export function getMusicApiUsdPerMinute({ featureFlags }: PricingDataOptions = {}) {
  return featureFlags?.[PRICING_FEATURE_FLAGS.MUSIC_PRICING_V2]
    ? MUSIC_PRICING_V2.apiUsdPerMinute
    : MUSIC_PRICING_LEGACY.apiUsdPerMinute;
}

/** Creative platform music overage USD per minute */
export function getMusicCreativeOverageUsdPerMinute(tier: Tier, { featureFlags }: PricingDataOptions = {}) {
  const creditsPerMinute = featureFlags?.[PRICING_FEATURE_FLAGS.MUSIC_PRICING_V2]
    ? MUSIC_PRICING_V2.creativeCreditsPerMinute
    : tier.platformConfig.creative.music.creditsPerMinute;
  return getMusicCreativeOverageUsdPerMinuteFromCredits(tier, creditsPerMinute);
}

/** Creative platform music included minutes */
export function getMusicCreativeIncludedMinutes(tier: Tier, options: PricingDataOptions = {}) {
  const creative = tier.platformConfig.creative;
  if (creative.music.overrideIncludedMinutes !== undefined) {
    return creative.music.overrideIncludedMinutes;
  }
  const creditsPerMinute = getMusicCreativeCreditsPerMinute(options);
  return getCreativeIncludedUnits(creditsPerMinute, tier);
}

/** API platform music included minutes */
export function getMusicApiIncludedMinutes(tier: Tier, { featureFlags }: PricingDataOptions = {}) {
  if (featureFlags?.[PRICING_FEATURE_FLAGS.MUSIC_PRICING_V2]) {
    const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
    const apiUsdPerMinute = MUSIC_PRICING_V2.apiUsdPerMinute;
    if (subscriptionPrice && apiUsdPerMinute > 0) {
      return Math.round(subscriptionPrice / apiUsdPerMinute);
    }
  }
  return tier.products.music.limits.includedMinutes;
}
